/**
 * @description Root application component for Lynx Admin portal
 *
 * Key features:
 * - Wristband OAuth authentication via AuthProvider
 * - Role-guarded routing (superadmin only)
 * - AdminLayout with persistent sidebar navigation
 * - Tenant pages: List, Create, Detail (Phase 3 complete)
 * - Channel Partner pages: List, Create, Detail, Create Sales Org under CP (LYN-323)
 * - Audit Logs page (PCI 10.2 audit trail review)
 * - Gateway config page (Integrations/Gateways — platform-level gateway CRUD)
 *
 * @dependencies
 * - AuthContext: Authentication state management
 * - AdminLayout: Sidebar + header layout wrapper
 * - Auth components: SignIn, AuthCallback
 *
 * @notes
 * - All routes except /sign-in and /auth/callback require authentication
 * - Default redirect sends authenticated users to /channel-partners
 *   until a Super Admin dashboard exists
 * - All integration pages now have real implementations
 */

import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import config from './services/api/config';
import { SignIn, AuthCallback } from './components/auth';
import AdminLayout from './components/layouts/AdminLayout';
import TenantList from './pages/tenants/TenantList';
import TenantCreate from './pages/tenants/TenantCreate';
import TenantDetail from './pages/tenants/TenantDetail';
import ChannelPartnerList from './pages/channel-partners/ChannelPartnerList';
import ChannelPartnerCreate from './pages/channel-partners/ChannelPartnerCreate';
import ChannelPartnerDetail from './pages/channel-partners/ChannelPartnerDetail';
import ChannelPartnerSalesOrgCreate from './pages/channel-partners/ChannelPartnerSalesOrgCreate';
import AuditLogs from './pages/security/AuditLogs';
import PlatformSettings from './pages/settings/PlatformSettings';
import EmailProviderConfig from './pages/integrations/EmailProviderConfig';
import GatewayConfig from './pages/integrations/GatewayConfig';
import { ADMIN_DEFAULT_PATH } from './utils/adminNavActive';

/**
 * ProtectedRoute - Renders children only if authenticated
 * Redirects to /sign-in if not authenticated
 */
function ProtectedRoute({ children }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return children;
}

/**
 * AppRoutes - All application routes
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected admin routes — wrapped in AdminLayout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        {/* Tenants */}
        <Route path="tenants" element={<TenantList />} />
        <Route path="tenants/new" element={<TenantCreate />} />
        <Route path="tenants/:id" element={<TenantDetail />} />

        {/* Channel Partners (peer of Tenants — SOs stay under Tenants) */}
        <Route path="channel-partners" element={<ChannelPartnerList />} />
        <Route path="channel-partners/new" element={<ChannelPartnerCreate />} />
        <Route path="channel-partners/:channelPartnerId/sales-orgs/new" element={<ChannelPartnerSalesOrgCreate />} />
        <Route path="channel-partners/:id" element={<ChannelPartnerDetail />} />

        {/* Integrations (Phase 4) */}
        <Route path="integrations/gateways" element={<GatewayConfig />} />
        <Route path="integrations/email" element={<EmailProviderConfig />} />

        {/* Settings */}
        <Route path="settings" element={<PlatformSettings />} />

        {/* Audit Logs */}
        <Route path="audit-logs" element={<AuditLogs />} />

        {/* Default redirect — Channel Partners until a dashboard exists */}
        <Route index element={<Navigate to={ADMIN_DEFAULT_PATH.replace(/^\//, '')} replace />} />
        <Route path="*" element={<Navigate to={ADMIN_DEFAULT_PATH.replace(/^\//, '')} replace />} />
      </Route>
    </Routes>
  );
}

/**
 * App - Root component
 */
function App() {
  // Load platform favicon from platform config (public endpoint, no auth needed)
  useEffect(() => {
    fetch(`${config.API_URL}/public/platform-config`)
      .then(res => res.json())
      .then(({ data }) => {
        const faviconUrl = data?.branding?.assets?.faviconUrl;
        if (faviconUrl) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = faviconUrl;
        }
      })
      .catch(() => {});
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
